import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LogOut, Plus, Pencil, Trash2, X, Package, ShoppingBag, IndianRupee, Layers, Star } from "lucide-react";
import { api, inr, formatApiErrorDetail, authHeader } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Logo from "../components/Logo";

const CATEGORIES = ["Necklaces", "Earrings", "Rings", "Bracelets"];
const STATUSES = ["confirmed", "shipped", "delivered", "cancelled"];
const EMPTY_PRODUCT = {
  name: "", category: "Necklaces", price: "", mrp: "", description: "", images: "",
  stock: 10, featured: false, rating: 4.7, detail_base: "Brass alloy", detail_plating: "2-micron 18k gold", detail_stones: "Cubic zirconia",
};

const Admin = () => {
  const { token, login, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!token) return;
    api.get("/admin/stats", { headers: authHeader() }).then((r) => setStats(r.data)).catch(() => {});
  }, [token, orders.length, products.length]);

  if (!token) return <AdminLogin onLogin={login} />;

  return (
    <main className="min-h-screen bg-[#0A0A0B]" data-testid="admin-dashboard">
      <header className="border-b border-[#d4af37]/20 bg-[#0c0c0e] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="text-xs tracking-[0.2em] uppercase text-[#d4af37]/70 hidden sm:block">Admin Studio</span>
            <button onClick={logout} className="flex items-center gap-2 text-xs uppercase tracking-wide text-zinc-400 hover:text-red-400 transition-colors" data-testid="admin-logout-button">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
          {[
            { icon: ShoppingBag, label: "Orders", value: stats?.total_orders ?? "—" },
            { icon: Package, label: "Paid Orders", value: stats?.paid_orders ?? "—" },
            { icon: IndianRupee, label: "Revenue", value: stats ? inr(stats.revenue) : "—" },
            { icon: Layers, label: "Products", value: stats?.products ?? "—" },
          ].map((s) => (
            <div key={s.label} className="rounded-xl border border-[#d4af37]/20 bg-[#101013] p-5">
              <s.icon size={18} className="text-[#d4af37]" strokeWidth={1.6} />
              <p className="text-2xl font-serif text-zinc-100 mt-3" data-testid={`admin-stat-${s.label.toLowerCase().replace(" ", "-")}`}>{s.value}</p>
              <p className="text-[11px] tracking-[0.18em] uppercase text-zinc-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2 mb-8">
          {["orders", "products"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`px-6 py-2.5 rounded-full text-xs tracking-[0.16em] uppercase border transition-all ${
                tab === t ? "bg-[#d4af37] text-[#0a0a0b] border-[#d4af37] font-semibold" : "border-[#d4af37]/25 text-zinc-400 hover:text-[#f3e5ab] hover:border-[#d4af37]/60"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {tab === "orders" ? (
          <OrdersTable orders={orders} setOrders={setOrders} token={token} />
        ) : (
          <ProductsAdmin products={products} setProducts={setProducts} token={token} onRefresh={() => api.get("/admin/stats", { headers: authHeader() }).then((r) => setStats(r.data))} />
        )}
      </div>
    </main>
  );
};

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onLogin(email, password);
      toast.success("Welcome back");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center px-5 bg-[#0A0A0B]" data-testid="admin-login">
      <motion.form
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        onSubmit={submit}
        className="w-full max-w-sm rounded-2xl border border-[#d4af37]/25 bg-[#101013] p-8"
      >
        <div className="flex justify-center mb-6"><Logo size={40} wordClass="text-2xl" /></div>
        <p className="text-center text-xs tracking-[0.2em] uppercase text-[#d4af37]/70 mb-8">Admin Studio</p>
        <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Email</label>
        <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="input-dark mb-5" data-testid="admin-login-email" placeholder="admin@aurelia.in" />
        <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Password</label>
        <input value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="input-dark mb-7" data-testid="admin-login-password" placeholder="••••••••" />
        <button type="submit" disabled={busy} className="btn-gold w-full" data-testid="admin-login-submit">
          {busy ? "Signing in..." : "Sign In"}
        </button>
      </motion.form>
    </main>
  );
};

const useAdminData = (token, tab, setter) => {
  useEffect(() => {
    if (!token || tab !== "orders") return;
    api.get("/admin/orders", { headers: authHeader() }).then((r) => setter(r.data)).catch((e) => {
      if (e.response?.status === 401) toast.error("Session expired — please log in again");
    });
  }, [token, tab]);
};

const OrdersTable = ({ orders, setOrders, token }) => {
  useAdminData(token, "orders", setOrders);

  const changeStatus = async (id, status) => {
    try {
      await api.patch(`/admin/orders/${id}`, { order_status: status }, { headers: authHeader() });
      setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, order_status: status } : o)));
      toast.success("Order updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="rounded-xl border border-[#d4af37]/20 overflow-hidden" data-testid="admin-orders-table">
      {orders.length === 0 ? (
        <p className="py-16 text-center text-sm text-zinc-500">No orders yet — they will appear here the moment one is placed.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead>
              <tr className="bg-[#0c0c0e] text-left text-[11px] tracking-[0.16em] uppercase text-zinc-500 border-b border-[#d4af37]/15">
                <th className="px-5 py-4">Order</th>
                <th className="px-5 py-4">Customer</th>
                <th className="px-5 py-4">Items</th>
                <th className="px-5 py-4">Total</th>
                <th className="px-5 py-4">Payment</th>
                <th className="px-5 py-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} className="border-b border-[#d4af37]/8 hover:bg-[#121215]/60 transition-colors" data-testid={`admin-order-row-${o.order_number}`}>
                  <td className="px-5 py-4">
                    <p className="text-zinc-200 font-medium">{o.order_number}</p>
                    <p className="text-xs text-zinc-600 mt-0.5">{new Date(o.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</p>
                  </td>
                  <td className="px-5 py-4">
                    <p className="text-zinc-300">{o.customer.name}</p>
                    <p className="text-xs text-zinc-600 mt-0.5">{o.customer.email}</p>
                  </td>
                  <td className="px-5 py-4 text-zinc-400">{o.items.reduce((s, i) => s + i.qty, 0)}</td>
                  <td className="px-5 py-4 text-zinc-200">{inr(o.total)}</td>
                  <td className="px-5 py-4">
                    <span className={`text-[11px] tracking-wide px-2.5 py-1 rounded-full border ${o.payment_status === "paid" ? "text-emerald-400 border-emerald-400/30 bg-emerald-400/10" : "text-amber-300 border-amber-300/30 bg-amber-300/10"}`} data-testid={`admin-order-payment-${o.order_number}`}>
                      {o.payment_status === "paid" ? "Paid" : "Pending"}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <select
                      value={o.order_status}
                      onChange={(e) => changeStatus(o.id, e.target.value)}
                      className="bg-[#121215] border border-[#d4af37]/25 rounded-lg px-3 py-2 text-xs text-zinc-200 cursor-pointer"
                      data-testid={`admin-order-status-${o.order_number}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s} className="bg-[#121215]">{s}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ProductsAdmin = ({ products, setProducts, token, onRefresh }) => {
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PRODUCT);

  const load = () => api.get("/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const openForm = (p = null) => {
    if (p) {
      setForm({
        name: p.name, category: p.category, price: p.price, mrp: p.mrp || "", description: p.description,
        images: (p.images || []).join("\n"), stock: p.stock, featured: p.featured, rating: p.rating,
        detail_base: p.details?.["Base Metal"] || "Brass alloy",
        detail_plating: p.details?.["Plating"] || "2-micron 18k gold",
        detail_stones: p.details?.["Stones"] || "",
      });
      setEditing(p);
    } else {
      setForm(EMPTY_PRODUCT);
      setEditing(null);
    }
    setShowForm(true);
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name: form.name,
      category: form.category,
      price: parseFloat(form.price) || 0,
      mrp: form.mrp ? parseFloat(form.mrp) : null,
      description: form.description,
      images: form.images.split("\n").map((s) => s.trim()).filter(Boolean),
      stock: parseInt(form.stock) || 0,
      featured: form.featured,
      rating: parseFloat(form.rating) || 4.5,
      details: {
        "Base Metal": form.detail_base,
        "Plating": form.detail_plating,
        ...(form.detail_stones ? { "Stones": form.detail_stones } : {}),
      },
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload, { headers: authHeader() });
      else await api.post("/admin/products", payload, { headers: authHeader() });
      toast.success(editing ? "Product updated" : "Product added");
      setShowForm(false);
      load();
      onRefresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`, { headers: authHeader() });
      toast.success("Product deleted");
      load();
      onRefresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const toggleFeatured = async (p) => {
    try {
      await api.put(`/admin/products/${p.id}`, { ...p, featured: !p.featured, mrp: p.mrp || null }, { headers: authHeader() });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const f = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const [uploading, setUploading] = useState(false);

  const uploadImages = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, {
          headers: { ...authHeader(), "Content-Type": "multipart/form-data" },
        });
        urls.push(data.url);
      }
      setForm((s) => ({
        ...s,
        images: [...(s.images ? s.images.split("\n").map((x) => x.trim()).filter(Boolean) : []), ...urls].join("\n"),
      }));
      toast.success(`${urls.length} image${urls.length > 1 ? "s" : ""} uploaded`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div data-testid="admin-products">
      <div className="flex justify-end mb-6">
        <button onClick={() => openForm()} className="btn-gold !px-5 !py-2.5" data-testid="admin-add-product-button">
          <Plus size={15} /> Add Product
        </button>
      </div>

      <div className="rounded-xl border border-[#d4af37]/20 overflow-hidden">
        {products.length === 0 ? (
          <p className="py-16 text-center text-sm text-zinc-500">No products yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="bg-[#0c0c0e] text-left text-[11px] tracking-[0.16em] uppercase text-zinc-500 border-b border-[#d4af37]/15">
                  <th className="px-5 py-4">Product</th>
                  <th className="px-5 py-4">Category</th>
                  <th className="px-5 py-4">Price</th>
                  <th className="px-5 py-4">Stock</th>
                  <th className="px-5 py-4">Featured</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b border-[#d4af37]/8 hover:bg-[#121215]/60 transition-colors" data-testid={`admin-product-row-${p.id}`}>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <img src={p.images?.[0]} alt={p.name} className="w-11 h-11 rounded-lg object-cover border border-[#d4af37]/20" />
                        <span className="text-zinc-200">{p.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-zinc-400">{p.category}</td>
                    <td className="px-5 py-4 text-zinc-200">{inr(p.price)}</td>
                    <td className="px-5 py-4">
                      <span className={p.stock <= 5 ? "text-amber-300" : "text-zinc-400"}>{p.stock}</span>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => toggleFeatured(p)}
                        data-testid={`admin-feature-toggle-${p.id}`}
                        className={`p-1.5 rounded-full transition-colors ${p.featured ? "text-[#d4af37]" : "text-zinc-600 hover:text-zinc-400"}`}
                        aria-label="Toggle featured"
                      >
                        <Star size={16} fill={p.featured ? "currentColor" : "none"} />
                      </button>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        <button onClick={() => openForm(p)} className="p-2 rounded-lg text-zinc-400 hover:text-[#f3e5ab] hover:bg-[#d4af37]/10" data-testid={`admin-edit-${p.id}`} aria-label="Edit product">
                          <Pencil size={15} />
                        </button>
                        <button onClick={() => del(p)} className="p-2 rounded-lg text-zinc-400 hover:text-red-400 hover:bg-red-400/10" data-testid={`admin-delete-${p.id}`} aria-label="Delete product">
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm overflow-y-auto py-10 px-5"
            data-testid="admin-product-modal"
          >
            <motion.form
              initial={{ scale: 0.95, y: 20, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onSubmit={save}
              className="max-w-2xl mx-auto rounded-2xl border border-[#d4af37]/30 bg-[#101013] p-8 relative"
            >
              <button type="button" onClick={() => setShowForm(false)} className="absolute top-4 right-4 p-2 rounded-full text-zinc-500 hover:text-zinc-200" data-testid="admin-product-modal-close" aria-label="Close">
                <X size={18} />
              </button>
              <h3 className="font-serif text-2xl text-zinc-100 mb-7">{editing ? "Edit Product" : "New Product"}</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Name</label>
                  <input required value={form.name} onChange={f("name")} className="input-dark" data-testid="admin-form-name" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Category</label>
                  <select value={form.category} onChange={f("category")} className="input-dark cursor-pointer" data-testid="admin-form-category">
                    {CATEGORIES.map((c) => <option key={c} className="bg-[#121215]">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Price (₹)</label>
                  <input required type="number" min="1" value={form.price} onChange={f("price")} className="input-dark" data-testid="admin-form-price" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">MRP (₹, optional)</label>
                  <input type="number" min="0" value={form.mrp} onChange={f("mrp")} className="input-dark" data-testid="admin-form-mrp" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Stock</label>
                  <input type="number" min="0" value={form.stock} onChange={f("stock")} className="input-dark" data-testid="admin-form-stock" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Description</label>
                  <textarea value={form.description} onChange={f("description")} rows="3" className="input-dark" data-testid="admin-form-description" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Product Photos</label>
                  <label
                    className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-[#d4af37]/40 bg-[#0c0c0e] py-7 cursor-pointer hover:border-[#d4af37]/80 hover:bg-[#d4af37]/5 transition-all"
                    data-testid="admin-image-upload-zone"
                  >
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      className="hidden"
                      onChange={(e) => { uploadImages(e.target.files); e.target.value = ""; }}
                      data-testid="admin-image-upload-input"
                    />
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" />
                    </svg>
                    <span className="text-xs text-zinc-400">{uploading ? "Uploading..." : "Click to upload photos — JPG, PNG or WebP, up to 5 MB"}</span>
                  </label>
                  {form.images && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {form.images.split("\n").map((u) => u.trim()).filter(Boolean).map((u, i) => (
                        <div key={i} className="relative group" data-testid={`admin-image-thumb-${i}`}>
                          <img src={u} alt="" className="w-16 h-16 object-cover rounded-lg border border-[#d4af37]/25" />
                          <button
                            type="button"
                            onClick={() => setForm((s) => ({ ...s, images: s.images.split("\n").map((x) => x.trim()).filter(Boolean).filter((_, j) => j !== i).join("\n") }))}
                            className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            data-testid={`admin-image-remove-${i}`}
                            aria-label="Remove image"
                          >×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <details className="mt-3">
                    <summary className="text-[11px] text-zinc-600 cursor-pointer hover:text-zinc-400">Or paste image URLs (one per line)</summary>
                    <textarea value={form.images} onChange={f("images")} rows="3" className="input-dark mt-2" data-testid="admin-form-images" placeholder="https://..." />
                  </details>
                </div>
                <div>
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Base Metal</label>
                  <input value={form.detail_base} onChange={f("detail_base")} className="input-dark" data-testid="admin-form-base-metal" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Plating</label>
                  <input value={form.detail_plating} onChange={f("detail_plating")} className="input-dark" data-testid="admin-form-plating" />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs tracking-[0.14em] uppercase text-zinc-500 block mb-2">Stones</label>
                  <input value={form.detail_stones} onChange={f("detail_stones")} className="input-dark" data-testid="admin-form-stones" />
                </div>
                <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={form.featured} onChange={(e) => setForm((s) => ({ ...s, featured: e.target.checked }))} className="accent-[#d4af37] w-4 h-4" data-testid="admin-form-featured" />
                  <span className="text-sm text-zinc-300">Featured (Signature Edit on homepage)</span>
                </label>
              </div>
              <div className="mt-8 flex gap-3">
                <button type="submit" className="btn-gold flex-1" data-testid="admin-form-save-button">{editing ? "Save Changes" : "Add Product"}</button>
                <button type="button" onClick={() => setShowForm(false)} className="btn-ghost" data-testid="admin-form-cancel-button">Cancel</button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Admin;
